import {
    MAX_LENGTH_CARDHOLDER_NAME,
    MIN_LENGTH_LATIN,
} from 'app/constants/baseConstants';

/**
 * Заменяет в исходной строке source символы начиная со startIndex по endIndex на символ symbol
 * @param source исходная строка
 * @param startIndex индекс начала (начиная с нуля)
 * @param endIndex индекс окончания (начиная с нуля)
 * @param symbol символ подстановки
 */

export function replaceSymbols(
    source: string,
    startIndex: number,
    endIndex: number,
    symbol: string,
): string {
    const prefix = source.slice(0, startIndex);
    const suffix = source.slice(endIndex + 1);

    let result = prefix;

    for (let i = startIndex; i <= endIndex; i++) {
        result += symbol;
    }

    result += suffix;
    return result;
}

/**
 * Вставить в заданную строку символы согласно маске.
 * Значащие символы маски - 'X'
 * При несоответствии количества символов в исходной строке и количества значащих символов в маске -
 * возвращает исходуную строку
 *
 * Пример
 * mask('+375331112233', 'XXXX XX XXX XX XX') => +375 33 111 22 33
 * @param source
 * @param _mask
 */
export function mask(source: string, _mask: string): string {
    const significantSymbol = 'X';

    let significantSymbolsCount = 0;
    for (let i = 0; i < _mask.length; i++) {
        if (_mask[i] === significantSymbol) {
            significantSymbolsCount++;
        }
    }

    if (source.length !== significantSymbolsCount) {
        return source;
    }

    let result = [];
    let i = 0;
    let j = 0;

    while (i < source.length) {
        if (_mask[i + j] === significantSymbol) {
            result.push(source[i]);
            i++;
        } else {
            result.push(_mask[i + j]);
            j++;
        }
    }

    return result.join('');
}

/**
 * Функция шифрования номера телефона. Алгоритм шифрования:
 *
 * N = (<phone> - 121573834576) * 17 - 597;
 *
 * @param phoneNumber телефонный номер
 */
export function encryptPhoneNumber(phoneNumber: string): string {
    const numericValue = Number(phoneNumber.substring(1));
    const encryptedValue = (numericValue - 121573834576) * 17 - 597;
    return String(encryptedValue);
}

/**
 * Функция дешифрования номера телефона. Алгоритм дешифрования
 *
 * phone = (N + 597) / 17 + 121573834576;
 *
 * @param encryptedPhoneNumber
 */
// function decryptPhoneNumber(encryptedPhoneNumber: string): string {
//     const numericValue = Number(encryptedPhoneNumber);
//     const decryptedValue = (numericValue + 597) / 17 + 121573834576;
//     return '+' + String(decryptedValue);
// }

const CARD_HOLDER_DIVIDER = '#';

/**
 * Сокращает строку имя-фамилия по первой букве имени
 * @param name
 * @param surname
 */
export const getWithShortName = (name: string, surname: string) =>
    `${
        name.trim().slice(0, 2) || ''
    }${CARD_HOLDER_DIVIDER}${surname.trim()}`.substring(
        0,
        MAX_LENGTH_CARDHOLDER_NAME,
    );

/**
 * Обрезает строку имя-фамилия по максимально допустимому количеству символов для поля
 * @param name
 * @param surname
 */
export const getWithShortSurname = (name?: string, surname?: string) => {
    if (!name || !surname) {
        return '';
    }

    // Минимальная длина фамилии должна составлять MIN_LENGTH_LATIN символов
    const maxNameLength =
        MAX_LENGTH_CARDHOLDER_NAME -
        CARD_HOLDER_DIVIDER.length -
        MIN_LENGTH_LATIN;

    return `${name
        .trim()
        .slice(
            0,
            maxNameLength,
        )}${CARD_HOLDER_DIVIDER}${surname.trim()}`.substring(
        0,
        MAX_LENGTH_CARDHOLDER_NAME,
    );
};

export const getConcatedCardHolderName = (
    name: string = '',
    surname: string = '',
) => {
    return `${name}${CARD_HOLDER_DIVIDER}${surname}`;
};

/**
 * Отдает строку cardholder name для отображения, заменяя символ склейки мени и фамилии на пробел
 * @param cardHolderName
 * @returns cardholder name для отображения
 */
export const getDividedCardHolderName = (cardHolderName: string) => {
    return cardHolderName.replace(CARD_HOLDER_DIVIDER, ' ');
};

/**
 * Преобразует строку к формату:
 * первый символ - заглавная буква
 * остальные символы - прописные буквы
 * @param str
 */
export const getWithFirstLetterCapital = (str: string) => {
    return `${str.charAt(0).toUpperCase()}${str.slice(1).toLowerCase()}`;
};

const TRANSLITERATION_MAP = new Map([
    ['А', 'A'],
    ['В', 'B'],
    ['С', 'C'],
    ['Е', 'E'],
    ['Н', 'H'],
    ['К', 'K'],
    ['М', 'M'],
    ['О', 'O'],
    ['Р', 'P'],
    ['Т', 'T'],
    ['У', 'Y'],
    ['Х', 'X'],
]);

/**
 * Переводит символ, вписанный на кириллической раскладке, в латиницу
 * @param char символ на кириллице
 * @returns соответствующий символ на латинице
 */
export const transliterationMapper = (char: string) =>
    TRANSLITERATION_MAP.get(char) || char;
